import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import * as I from 'immutable';
import React from 'react';
import {Link} from 'react-router-dom';

import {AlertTypeChip} from 'app/components/Alerts/alertTypes';
import {getDefaultVegetationDropFormState} from 'app/components/Alerts/useAlertForm';
import {SidebarSection} from 'app/components/Sidebar';
import {AlertPolicy, ApiFeature} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {useUserInfo} from 'app/providers/AuthProvider';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {recordEvent} from 'app/tools/Analytics';
import * as CONSTANTS from 'app/utils/constants';
import {featureInRegridCoverage} from 'app/utils/featureCollectionUtils';
import featureFlags from 'app/utils/featureFlags';
import * as featureUtils from 'app/utils/featureUtils';
import {S2_NDVI} from 'app/utils/layers';
import * as layerUtils from 'app/utils/layerUtils';
import {getOrgPrefix} from 'app/utils/organizationUtils';
import {atLeastUserRole} from 'app/utils/userUtils';

import {AlertConfigurationModal} from './AlertConfigurationModal';
import {useAlertPolicies, useParcelEligibility} from './AlertPolicyProvider';
import {
  formatCompositeExpression,
  isDefaultUpstreamVegetationDropAlertPolicy,
} from './alertPolicyUtils';
import cs from './styles.module.scss';

export interface AlertsPoliciesPanelProps {
  updateFeature: FeaturesActions['updateFeature'];
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  feature: I.ImmutableOf<ApiFeature>;
  allFeatures: ApiFeature[];
  enabledLookoutCount: number;
}

export const DEFAULT_UPSTREAM_VEG_ALERT_POLICY = {
  ...getDefaultVegetationDropFormState(),
  name: CONSTANTS.ALERT_VEGETATION_DROP_DEFAULT_POLICY_NAME,
  description:
    'Detect areas where a significant decrease in vegetation vigor has occurred compared to the same period in prior years.',
};

export const AlertsPoliciesPanel: React.FC<React.PropsWithChildren<AlertsPoliciesPanelProps>> = ({
  updateFeature,
  featureCollection,
  feature,
  allFeatures,
  enabledLookoutCount,
}) => {
  const inRegridCoverage = featureInRegridCoverage(feature);
  const enrolledLayerKeys = layerUtils.getEnrolledLayerKeysForFeatureCollection(featureCollection);
  const [configurationModalState, setConfigurationModalState] = React.useState<{
    isOpen: boolean;
    alertPolicy?: AlertPolicy;
  }>({isOpen: false});
  const [organization, profile] = useUserInfo();
  const canModifyAlertPolicies = atLeastUserRole(CONSTANTS.USER_ROLE_REGULAR, profile!);
  const parcelAlertsInPlan = featureFlags.PARCEL_CHANGE_ALERTS(organization!);

  const {
    alertPolicies,
    actions: {enrollFeaturesInAlertPolicy, unenrollFeaturesInAlertPolicy, archiveAlertPolicy},
    meta: {isFetching: isFetchingAlerts},
  } = useAlertPolicies();

  const defaultVegetationDropAlertPolicy = alertPolicies.find((alertPolicy) =>
    isDefaultUpstreamVegetationDropAlertPolicy(alertPolicy)
  );

  const vegetationDropAlertsEnabled = !!defaultVegetationDropAlertPolicy?.enrollments?.some(
    (enrollment) => enrollment.featureId === feature.get('id')
  );

  const ownershipChangeAlertsEnabled = featureUtils.keyInAppProperties(
    feature,
    CONSTANTS.ALERT_OWNERSHIP_CHANGE_ENROLLMENT_KEY
  );

  const parcelAreaAlertsEnabled = featureUtils.keyInAppProperties(
    feature,
    CONSTANTS.ALERT_PARCEL_BOUNDARY_CHANGE_ENROLLMENT_KEY
  );

  const featureCollectionId = featureCollection.get('id');
  const featureId = feature.get('id');
  const {
    data: parcelEligibility,
    isPending: isParcelEligibilityPending,
    isError: isParcelEligibilityError,
  } = useParcelEligibility(featureCollectionId, featureId, {
    enabled: inRegridCoverage && parcelAlertsInPlan,
  });

  const enrollmentBlockedTooltip = React.useMemo<React.ReactElement | undefined>(() => {
    if (!inRegridCoverage || !parcelAlertsInPlan) return undefined;
    // If the eligibility request fails, fall through — the backend still enforces the cap,
    // so it's better to let the user try than to leave the toggle permanently blocked.
    if (isParcelEligibilityError) return undefined;
    if (isParcelEligibilityPending || !parcelEligibility) {
      return <span>Checking parcel coverage…</span>;
    }
    if (!parcelEligibility.eligible) {
      return (
        <span>
          This property intersects {parcelEligibility.parcelCount.toLocaleString()} parcels, above
          the {CONSTANTS.MAX_REGRID_PARCELS_PER_PROPERTY.toLocaleString()}-parcel cap. Reach out to{' '}
          <a href="mailto:lens@upstream.tech">lens@upstream.tech</a> to enable parcel-change alerts
          for this property.
        </span>
      );
    }
    return undefined;
  }, [
    inRegridCoverage,
    parcelAlertsInPlan,
    isParcelEligibilityPending,
    isParcelEligibilityError,
    parcelEligibility,
  ]);
  const enrollmentBlocked = !!enrollmentBlockedTooltip;

  const {actions: alertPolicyActions} = useAlertPolicies();

  const onVegetationEnrollmentChange = React.useCallback(async () => {
    const nextIsEnabled = !vegetationDropAlertsEnabled;
    // Handle Veg Drop AlertPolicy creation/enrollment:
    //If our default policy already exists, enroll or unenroll based on toggle
    if (defaultVegetationDropAlertPolicy) {
      if (nextIsEnabled) {
        enrollFeaturesInAlertPolicy(defaultVegetationDropAlertPolicy, [feature.get('id')]);
      } else {
        unenrollFeaturesInAlertPolicy(defaultVegetationDropAlertPolicy, [feature.get('id')]);
      }
      // If our default policy hasn't been created yet, create it and enroll the feature
    } else {
      if (nextIsEnabled) {
        const newDefaultVegDropAlertPolicy = await alertPolicyActions.createAlertPolicy(
          featureCollection,
          DEFAULT_UPSTREAM_VEG_ALERT_POLICY
        );
        enrollFeaturesInAlertPolicy(
          {...newDefaultVegDropAlertPolicy, enrollments: []} as AlertPolicy,
          [feature.get('id')]
        );
      } // if we are turning OFF the toggle, don't create a new policy.
    }
    nextIsEnabled ? recordEvent('Enabled Lookout policy') : recordEvent('Disabled Lookout policy');
  }, [
    vegetationDropAlertsEnabled,
    defaultVegetationDropAlertPolicy,
    enrollFeaturesInAlertPolicy,
    feature,
    featureCollection,
    updateFeature,
    unenrollFeaturesInAlertPolicy,
    alertPolicyActions,
  ]);

  const onOwnershipEnrollmentChange = React.useCallback(() => {
    const nextIsEnabled = !ownershipChangeAlertsEnabled;
    updateFeature(
      feature.get('id'),
      featureUtils.makeAlertEnrollmentAppPropertyPatch(
        CONSTANTS.ALERT_OWNERSHIP_CHANGE_ENROLLMENT_KEY,
        nextIsEnabled
      )
    );
    nextIsEnabled ? recordEvent('Enabled Lookout policy') : recordEvent('Disabled Lookout policy');
  }, [feature, ownershipChangeAlertsEnabled, updateFeature]);

  const onParcelAreaEnrollmentChange = React.useCallback(() => {
    const nextIsEnabled = !parcelAreaAlertsEnabled;
    updateFeature(
      feature.get('id'),
      featureUtils.makeAlertEnrollmentAppPropertyPatch(
        CONSTANTS.ALERT_PARCEL_BOUNDARY_CHANGE_ENROLLMENT_KEY,
        nextIsEnabled
      )
    );
    nextIsEnabled ? recordEvent('Enabled Lookout policy') : recordEvent('Disabled Lookout policy');
  }, [feature, parcelAreaAlertsEnabled, updateFeature]);

  const unenrolledInS2Vegetation = !enrolledLayerKeys.includes(S2_NDVI);

  const vegTooltipMessage: React.ReactElement | undefined = React.useMemo(() => {
    if (!canModifyAlertPolicies)
      return <span>Contact your organization administrator to modify alert policies.</span>;

    if (unenrolledInS2Vegetation) {
      if (vegetationDropAlertsEnabled) {
        return (
          <div>
            Vegetation drop alerts are enabled, but you are not enrolled in the necessary vegetation
            layers. Add S2 Vegetation from the{' '}
            <Link to={`/${getOrgPrefix(organization!)}/settings/layers`} target="_blank">
              Layers Library page
            </Link>{' '}
            to finish enrollment.
          </div>
        );
      } else {
        return (
          <div>
            First, add the S2 Vegetation from the{' '}
            <Link
              to={`/${getOrgPrefix(organization!)}/settings/layers`}
              onClick={() => recordEvent('Followed missing layer warning')}
              target="_blank"
            >
              Layers Library page
            </Link>
            . Then, use this switch to activate vegetation alerts for this property.
          </div>
        );
      }
    }

    return (
      <span>
        Automatically detect unseasonal vegetation declines across your property, year-round. To set
        spatial or seasonal rules, click "Create a policy."
      </span>
    );
  }, [unenrolledInS2Vegetation, canModifyAlertPolicies, vegetationDropAlertsEnabled, organization]);

  const cantEditToolTip: React.ReactElement | undefined = React.useMemo(() => {
    if (canModifyAlertPolicies) return undefined;

    return <span>Contact your organization administrator to edit alert policies.</span>;
  }, [canModifyAlertPolicies]);

  const defaultAlerts = React.useMemo(
    () =>
      [
        <AlertPolicyRow
          key="default-veg-policy"
          icon={<AlertTypeChip type="vegetationDrop" />}
          name={DEFAULT_UPSTREAM_VEG_ALERT_POLICY.name}
          isEnabled={vegetationDropAlertsEnabled}
          enrollmentHandler={onVegetationEnrollmentChange}
          switchDisabled={
            (unenrolledInS2Vegetation && !vegetationDropAlertsEnabled) || !canModifyAlertPolicies
          }
          tooltipMessage={vegTooltipMessage}
          actionsMenu={
            <PolicyInfoLinkAction href="https://lens.tech/support/article/115-vegetation-drop-notifications/" />
          }
        />,
        inRegridCoverage && parcelAlertsInPlan && (
          <React.Fragment key="default-parcel-policies">
            <AlertPolicyRow
              key="default-ownership-policy"
              icon={<AlertTypeChip type="ownershipChange" />}
              name="Change in parcel ownership"
              isEnabled={ownershipChangeAlertsEnabled}
              enrollmentHandler={onOwnershipEnrollmentChange}
              switchDisabled={
                !canModifyAlertPolicies || (enrollmentBlocked && !ownershipChangeAlertsEnabled)
              }
              tooltipMessage={
                enrollmentBlocked && !ownershipChangeAlertsEnabled
                  ? enrollmentBlockedTooltip
                  : cantEditToolTip
              }
              actionsMenu={
                <PolicyInfoLinkAction href="https://lens.tech/support/article/129-parcel-owner-alerts" />
              }
            />
            <AlertPolicyRow
              key="default-parcel-area-policy"
              icon={<AlertTypeChip type="parcelAreaChange" />}
              name="Change in parcel area"
              isEnabled={parcelAreaAlertsEnabled}
              enrollmentHandler={onParcelAreaEnrollmentChange}
              switchDisabled={
                !canModifyAlertPolicies || (enrollmentBlocked && !parcelAreaAlertsEnabled)
              }
              tooltipMessage={
                enrollmentBlocked && !parcelAreaAlertsEnabled
                  ? enrollmentBlockedTooltip
                  : cantEditToolTip
              }
              actionsMenu={
                <PolicyInfoLinkAction href="https://lens.tech/support/article/129-parcel-owner-alerts" />
              }
            />
          </React.Fragment>
        ),
      ].filter(Boolean) as React.ReactElement[],
    [
      vegetationDropAlertsEnabled,
      onVegetationEnrollmentChange,
      unenrolledInS2Vegetation,
      canModifyAlertPolicies,
      vegTooltipMessage,
      inRegridCoverage,
      parcelAlertsInPlan,
      ownershipChangeAlertsEnabled,
      onOwnershipEnrollmentChange,
      cantEditToolTip,
      parcelAreaAlertsEnabled,
      onParcelAreaEnrollmentChange,
      enrollmentBlocked,
      enrollmentBlockedTooltip,
    ]
  );

  const enrollOrUnenroll = React.useCallback(
    (alertPolicy: AlertPolicy, isEnabled: boolean, featureId: number) => {
      if (isEnabled) {
        unenrollFeaturesInAlertPolicy(alertPolicy, [featureId]);
        recordEvent('Disabled Lookout policy');
      } else {
        enrollFeaturesInAlertPolicy(alertPolicy, [featureId]);
        recordEvent('Enabled Lookout policy');
      }
    },
    [unenrollFeaturesInAlertPolicy, enrollFeaturesInAlertPolicy]
  );

  const renderAlertPolicyRow = React.useCallback(
    (alertPolicy: AlertPolicy) => {
      const featureId = feature.get('id');
      const isEnabled = !!alertPolicy.enrollments?.find(
        (enrollment) => enrollment?.featureId === featureId
      );

      return (
        <AlertPolicyRow
          key={alertPolicy.id}
          name={alertPolicy.name}
          icon={<AlertTypeChip type="customAlert" />}
          isEnabled={isEnabled}
          switchDisabled={!canModifyAlertPolicies}
          tooltipMessage={cantEditToolTip}
          enrollmentHandler={() => enrollOrUnenroll(alertPolicy, isEnabled, featureId)}
          actionsMenu={
            <PolicyConfigurationOptionsPanel
              alertPolicy={alertPolicy}
              canModifyAlertPolicies={canModifyAlertPolicies}
              editCallback={() =>
                setConfigurationModalState({
                  isOpen: true,
                  alertPolicy,
                })
              }
              deleteCallback={() => {
                if (confirm(`Delete ${alertPolicy.name}?`)) {
                  archiveAlertPolicy(alertPolicy.id);
                }
              }}
            />
          }
        />
      );
    },
    [feature, canModifyAlertPolicies, enrollOrUnenroll, archiveAlertPolicy, cantEditToolTip]
  );

  const renderedAlertPolicies = React.useMemo(() => {
    return defaultAlerts.concat(
      alertPolicies
        .filter((alertPolicy) => !isDefaultUpstreamVegetationDropAlertPolicy(alertPolicy))
        .map(renderAlertPolicyRow)
    );
  }, [defaultAlerts, alertPolicies, renderAlertPolicyRow]);

  return (
    <>
      <AlertConfigurationModal
        featureCollection={featureCollection}
        allFeatures={allFeatures}
        selectedFeatureId={feature.get('id')}
        alertPolicy={configurationModalState.alertPolicy}
        isOpen={configurationModalState.isOpen}
        onClose={() => setConfigurationModalState({isOpen: false})}
        canModifyAlertPolicies={canModifyAlertPolicies}
      />
      <SidebarSection
        title="Lookouts"
        meta={<span className={cs.countChip}>{`${enabledLookoutCount} on`}</span>}
        bordered={false}
        bodyClassName={cs.policySectionBody}
        rightElement={
          <B.Tooltip content={cantEditToolTip} disabled={!cantEditToolTip}>
            <B.Button
              outlined
              intent="primary"
              icon="plus"
              text="New policy"
              disabled={!canModifyAlertPolicies}
              onClick={() => setConfigurationModalState({isOpen: true})}
            />
          </B.Tooltip>
        }
      >
        {isFetchingAlerts ? (
          <B.Spinner key="alert-policy-fetching-spinner" />
        ) : (
          renderedAlertPolicies
        )}
      </SidebarSection>
    </>
  );
};

export interface AlertPolicyRowProps {
  name: string;
  icon?: React.ReactElement;
  className?: string;
  isEnabled: boolean;
  enrollmentHandler: () => void;
  tooltipMessage?: React.ReactElement;
  switchDisabled?: boolean;
  actionsMenu?: React.ReactElement;
}

export const AlertPolicyRow = ({
  name,
  icon,
  className,
  isEnabled,
  enrollmentHandler,
  tooltipMessage,
  switchDisabled,
  actionsMenu,
}: AlertPolicyRowProps) => {
  return (
    <div className={classnames(cs.alertPolicyRow, className)}>
      {icon}
      <span className={cs.alertPolicyRowName}>{name}</span>
      <span className={cs.alertPolicyRowControls}>
        {actionsMenu}
        <B.Tooltip
          content={<div className={cs.tooltip}>{tooltipMessage}</div>}
          disabled={!tooltipMessage}
          position={B.PopoverPosition.BOTTOM_RIGHT}
          modifiers={{preventOverflow: {enabled: false}}}
        >
          <B.Switch
            alignIndicator="right"
            disabled={switchDisabled}
            checked={isEnabled}
            onChange={enrollmentHandler}
          />
        </B.Tooltip>
      </span>
    </div>
  );
};

const PolicyInfoLinkAction = ({href}: {href: string}) => (
  <B.AnchorButton
    className={cs.alertPolicyRowAction}
    small
    minimal
    icon={<B.Icon icon="info-sign" size={14} />}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
  />
);

export interface PolicyConfigurationOptionsPanelProps {
  editCallback: () => void;
  deleteCallback: () => void;
  canModifyAlertPolicies: boolean;
  alertPolicy: AlertPolicy;
}

export const PolicyConfigurationOptionsPanel: React.FC<
  React.PropsWithChildren<PolicyConfigurationOptionsPanelProps>
> = ({editCallback, deleteCallback, canModifyAlertPolicies, alertPolicy}) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  return (
    <B.Popover
      position={B.PopoverPosition.BOTTOM_LEFT}
      minimal
      interactionKind={B.PopoverInteractionKind.HOVER}
      isOpen={menuOpen}
      onInteraction={(isOpen) => setMenuOpen(isOpen)}
      onClose={() => setMenuOpen(false)}
      modifiers={{preventOverflow: {enabled: false}, hide: {enabled: false}}}
      content={
        <B.Menu style={{minWidth: 0}}>
          <B.MenuItem
            icon={canModifyAlertPolicies ? 'edit' : 'eye-open'}
            text={canModifyAlertPolicies ? 'View/Edit' : 'View'}
            onClick={() => editCallback()}
          />
          {alertPolicy.alertType === CONSTANTS.CUSTOM_ALERT_TYPE_COMPOSITE && (
            <B.MenuItem
              icon="function"
              text="Conditions"
              popoverProps={{
                position: B.PopoverPosition.RIGHT,
                interactionKind: B.PopoverInteractionKind.HOVER,
              }}
            >
              <div style={{padding: '1rem', maxWidth: '30rem'}}>
                {formatCompositeExpression(alertPolicy.sourceId, alertPolicy.properties)}
              </div>
            </B.MenuItem>
          )}
          {alertPolicy.description && (
            <B.MenuItem
              icon="info-sign"
              text={'Description'}
              popoverProps={{
                position: B.PopoverPosition.RIGHT,
                interactionKind: B.PopoverInteractionKind.HOVER,
              }}
            >
              <div style={{padding: '1rem', maxWidth: '30rem'}}>{alertPolicy.description}</div>
            </B.MenuItem>
          )}
          <B.MenuItem
            icon="trash"
            text="Delete"
            onClick={() => deleteCallback()}
            disabled={!canModifyAlertPolicies}
          />
        </B.Menu>
      }
    >
      <B.AnchorButton
        className={classnames(cs.alertPolicyRowAction, menuOpen && cs.visible)}
        small
        minimal
        icon={
          <B.Icon
            icon="more"
            size={14}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(true);
            }}
          />
        }
        active={menuOpen}
      />
    </B.Popover>
  );
};
